import { Checkbox, Select, UserSelect } from '@/components_deprecated'
import { PlainLink } from '@/components_deprecated/Link'
import { IssueRiskAspectSelect } from '@/components_deprecated/Select/IssueRiskAspectSelect'
import { Module, ModuleOptionsValue } from '@/modules/companies'
import type { UserNode } from '@/modules/users'
import type { ThumbnailUser } from '@/modules/users/UserThumbOrInitials'
import { format } from '@/utils/date-fns'
import { useModuleConfig, usePermissions } from '@/utils/hooks'
import { unlessLoadingDots } from '@/utils/loading'
import { PERMISSIONS } from '@/utils/permissions'
import { overloadColor } from '@/utils/style'
import { Icon } from '@ur-solutions/react-components'
import type { SelectOption } from '@ur-solutions/react-components'
import type {
  FormErrors,
  FormOnChangeFn,
  UpdateFormFn,
} from '@ur-solutions/react-hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import { set } from 'date-fns'
import React, { useEffect, useEffectEvent, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { useIssueSeverityOptions } from '../consts'
import type { EditIssueForm } from '../types'
import type {
  IssueCategoryNode,
  IssueRiskAspectNode,
  IssueSeverity,
  LocalSubcategory,
} from '../types.graphl'
import type { ShallowCategory } from './CategorySelect'
import { CategorySelect } from './CategorySelect'
import {
  IssueRelationField,
  useIssueRelations,
} from './issue-relations-selector'
import { DatePicker, TimePicker } from '@/components'

const ResponsibleWrapper = styled.div`
  && .--select-display {
    height: auto;
    min-height: 3rem;
  }

  && .--select-display-inner {
    flex-wrap: nowrap;
    align-items: flex-start;
  }

  && .--select-display-selected {
    color: ${props => props.theme.colors.black};
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    min-width: 0;

    > div {
      flex: 1 1 auto;
      min-width: 0;
    }
    .name {
      white-space: normal;
      word-break: break-word;
    }

    .--select-display-selected-clear {
      flex: 0 0 auto;
      align-self: center;
      margin-left: 0.4rem;
      max-height: 20px;
    }
  }
`

interface WrapperProps {
  editing: boolean
}

const COLS = 3

/**
 * Cell border styles for the COLS-wide grid.
 *
 * Each non-date cell gets a left + top border, then we suppress the left
 * border on column 1 and the top border on row 1 so the outer edges stay
 * clean. The `.date` cell is treated separately because it spans the full
 * width on its own row.
 */
const cellBorders = css`
  & > div:not(.date) {
    border-left: 1px solid ${props => props.theme.colors.gray8};
    border-top: 1px solid ${props => props.theme.colors.gray8};
  }
  & > div:not(.date):nth-child(${COLS}n + 1) {
    border-left: 0;
  }
  & > div:not(.date):nth-child(-n + ${COLS}) {
    border-top: 0;
  }
  & > .date {
    grid-column: 1 / -1;
    border-top: 1px solid ${props => props.theme.colors.gray8};
  }
`

/**
 * Ellipsis-truncate long Select labels so cells keep a stable height and
 * never push the grid wider than its container. The opened dropdown
 * (`.--select-curtain`) is intentionally left alone so the full option
 * names stay readable when picking.
 *
 * The deprecated Select renders this DOM:
 *   .--select-display (flex container; arrow icon sibling)
 *     .--select-display-inner (wraps the label span)
 *       .--select-display-selected (the label span)
 *     .--select-arrow-icon
 *
 * For text-overflow ellipsis to actually take effect every level on the
 * way down must allow shrinking (`min-width: 0`); the leaf span owns the
 * `overflow: hidden` + `text-overflow: ellipsis` rules.
 */
const selectLabelTruncation = css`
  .--select-display {
    min-width: 0;
    max-width: 100%;
  }
  .--select-display-inner {
    min-width: 0;
    overflow: hidden;
    flex: 1 1 0%;
  }
  .--select-display-selected,
  .--select-placeholder {
    display: block;
    min-width: 0;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

/**
 * On mobile the grid collapses into a vertical stack while editing so each
 * field gets full width. Display mode keeps the grid layout and just lets
 * the user scroll horizontally if the cell content is too wide.
 */
const mobileLayout = css<WrapperProps>`
  ${props => props.theme.media.mobile} {
    overflow-x: auto;

    ${props =>
      props.editing &&
      css`
        display: flex;
        flex-direction: column;

        & > div {
          border: 1px solid ${props => props.theme.colors.gray8} !important;
          border-width: 1px 0 0 0 !important;
          grid-column: auto;

          &.category {
            order: -1;
            border-width: 0 !important;
          }

          &.date {
            order: -1;
          }
        }
      `};
  }
`

const Wrapper = styled.section<WrapperProps>`
  grid-area: main;
  min-width: 0;
  max-width: 100%;

  display: grid;
  grid-template-columns: repeat(${COLS}, 1fr);

  span.unknown {
    color: ${props => props.theme.colors.red};
  }

  ${cellBorders};
  ${selectLabelTruncation};
  ${mobileLayout};
`
const Info = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem;
  gap: 0.5rem;
  min-width: 0;
  font-weight: 600;

  & > span {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    color: ${props => props.theme.colors.gray5};
  }

  p {
    margin: 0;
    font-size: 1.6em;
  }

  ${props => props.theme.media.mobile} {
    padding: 1rem;

    span {
      font-size: 0.7rem;
    }

    p {
      font-size: 1rem;
    }
  }
`

interface IssueStatusProps {
  closed: boolean
  archived: boolean
}

const IssueStatus = styled.div<IssueStatusProps>`
  display: flex;
  align-items: center;

  &::after {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-left: 0.5ch;
    margin-bottom: 3px;

    border-radius: 50%;
    background: ${props =>
      props.archived
        ? props.theme.colors.orange
        : props.closed
        ? props.theme.colors.green
        : props.theme.colors.red};
  }
`

const Severity = styled.div<{ severity: IssueSeverity | null }>`
  display: flex;
  align-items: center;

  ${props =>
    props.severity !== null &&
    `
    &::after {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      margin-left: 0.5ch;
      margin-bottom: 3px;

      border-radius: 50%;
      background: ${
        props.severity === 'LOW'
          ? props.theme.colors.green
          : props.severity === 'MEDIUM'
          ? props.theme.colors.orange
          : props.theme.colors.red
      };
    }
  `}
`

interface IssueCategoryProps {
  color: string
}

const IssueCategory = styled.div<IssueCategoryProps>`
  &::after {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-left: 0.5ch;
    margin-bottom: 3px;

    border-radius: 50%;
    background: ${props => overloadColor(props.color)};
  }
`
const Editor = styled.div`
  margin-top: 0.5rem;
  min-width: 0;
`
const DateEditor = styled(Editor)`
  display: flex;

  & > * + * {
    margin-left: 0.5rem;
  }
`
const TimeLabel = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: ${props => props.theme.colors.gray5};
`

const TimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

interface MainInfoProps {
  loading: boolean
  editing: boolean
  form: EditIssueForm
  errors: FormErrors<EditIssueForm>
  subCategory?: LocalSubcategory
  category:
    | (Pick<IssueCategoryNode, 'id' | 'name' | 'color'> & {
        responsibleUser: ThumbnailUser | null
      })
    | null
    | undefined
  date: string | undefined
  closedAt: string | null | undefined
  archivedAt: string | null | undefined
  reference: string | null | undefined
  user: ThumbnailUser | null | undefined
  responsible: ThumbnailUser | null | undefined
  caseHandler: ThumbnailUser | null | undefined
  severity: IssueSeverity | undefined
  riskAspect: IssueRiskAspectNode | null | undefined
  dateEnd: string | undefined

  requireSeverity: boolean
  requireRiskAspect: boolean

  onChange: FormOnChangeFn<EditIssueForm>
  updateForm: UpdateFormFn<EditIssueForm>
  subcategoryOptions: SelectOption<string, any>[]
  subcategoriesLoading: boolean
}

const RequiredMark = () => <span style={{ color: 'red' }}>*</span>

export const MainInfo: React.FC<MainInfoProps> = ({
  loading,
  editing,
  form,
  errors,
  subCategory,
  category,
  date,
  closedAt,
  archivedAt,
  reference,
  user,
  responsible,
  caseHandler,
  severity,
  riskAspect,
  dateEnd,
  requireSeverity,
  requireRiskAspect,

  onChange,
  updateForm,
  subcategoryOptions,
  subcategoriesLoading,
}) => {
  const translations = useTranslate({
    unknown: 'common.unknown',

    open: 'common.open-alt',
    closed: 'common.closed',
    archived: 'common.archived',

    status: 'common.status',
    subCategory: 'common.sub-category',
    category: 'common.category',
    reference: ['issues.reference', { abbreviate: false }],
    date: 'common.date',
    user: 'common.submitter',
    responsible: 'common.responsible',
    caseHandler: 'common.case-handler',
    dateRange: 'common.date-range',

    riskAspect: 'common.risk-aspect',

    none: 'common.none',
    severity: 'common.severity',
    severityGrades: {
      LOW: 'common.low',
      MEDIUM: 'common.medium',
      HIGH: 'common.high',
    },
    dataFrom: 'common.data-from',
    dataTo: 'common.data-to',
    addRelation: 'issues.add-relation',
  })

  const severityOptions = useIssueSeverityOptions()

  const [temporaryResponsible, setTemporaryResponsible] = useState<Pick<
    UserNode,
    'id' | 'fullName'
  > | null>(null)

  const { hasPermissions } = usePermissions()
  const canEdit = hasPermissions(PERMISSIONS.issues.change.issue)
  const showEditMark = editing && canEdit && !form.forInfringements
  const infringementModuleConfig = useModuleConfig(Module.INFRINGEMENTS)
  const userCanCloseOwnInfringement =
    infringementModuleConfig.getModuleOptionStatus(
      ModuleOptionsValue.INFRINGEMENTS_USER_CAN_CLOSE_OWN_INFRINGEMENT
    )

  const {
    visibleRelations,
    availableOptionalRelations,
    setRelationOption,
    addOptional,
    removeOptional,
  } = useIssueRelations(form.relationOptions, relationOptions =>
    updateForm({ relationOptions })
  )

  const handleSetTemporaryResponsible = useEffectEvent(
    (user: Pick<UserNode, 'id' | 'fullName'> | null) => {
      setTemporaryResponsible(user)
    }
  )

  useEffect(() => {
    handleSetTemporaryResponsible(editing ? responsible ?? null : null)
  }, [editing, responsible])

  function handleChangeCategory(id: string, category: ShallowCategory | null) {
    updateForm({
      category: id,
      severity: category?.defaultSeverity ?? form.severity,
    })
    setTemporaryResponsible(category?.responsibleUser ?? temporaryResponsible)
  }

  function handleChangeDate(date: Date, fieldName: keyof EditIssueForm) {
    const baseDate =
      form[fieldName] instanceof Date ? form[fieldName] : new Date()

    const newDate = set(date, {
      hours: baseDate.getHours(),
      minutes: baseDate.getMinutes(),
      seconds: baseDate.getSeconds(),
    })

    if (fieldName === 'date' && form.dateEnd && newDate > form.dateEnd) {
      onChange('dateEnd')(newDate)
    }

    onChange(fieldName)(newDate)
  }

  const unlessLoading = unlessLoadingDots(loading, translations.unknown)

  const issueStatus = useMemo(() => {
    if (typeof closedAt === 'undefined' && typeof archivedAt === 'undefined')
      return null

    const archived =
      archivedAt !== null ? ` (${translations.archived.toLowerCase()})` : ''
    let text = ''

    if (closedAt !== null) text = `${translations.closed}${archived}`
    else text = `${translations.open}${archived}`

    return (
      <IssueStatus closed={closedAt !== null} archived={archivedAt !== null}>
        {text}
      </IssueStatus>
    )
  }, [
    archivedAt,
    closedAt,
    translations.archived,
    translations.closed,
    translations.open,
  ])

  const issueCategory = useMemo(() => {
    if (!category) return '-'

    return <IssueCategory color={category.color}>{category.name}</IssueCategory>
  }, [category])

  const displaySeverity = severity ?? form.severity

  useEffect(() => {
    if (subcategoryOptions?.length > 0 || subcategoriesLoading) return

    if (form.subcategory !== null) {
      onChange('subcategory')(null)
    }
  }, [
    form.category,
    subcategoryOptions,
    subcategoriesLoading,
    form.subcategory,
    onChange,
  ])

  return (
    <Wrapper editing={editing}>
      <Info>
        <span>{translations.status}</span>
        {!editing ||
        (!userCanCloseOwnInfringement && form.forInfringements && !canEdit) ? (
          <p>{unlessLoading(issueStatus)}</p>
        ) : (
          <Editor>
            <Checkbox
              checked={form.closed}
              label={translations.closed}
              boxSize="2rem"
              onChange={onChange('closed')}
            />
          </Editor>
        )}
      </Info>

      <Info>
        <span>{translations.user}</span>
        <p>
          {unlessLoading(
            <PlainLink
              to={user ? `/users/${user?.id}` : '#'}
              $hoverColor="secondary"
            >
              {user?.fullName ?? '-'}
            </PlainLink>
          )}
        </p>
      </Info>

      <Info>
        <span>{translations.reference()}</span>
        <p>{unlessLoading(reference)}</p>
      </Info>

      <Info>
        <span>
          {translations.severity}{' '}
          {editing && canEdit && requireSeverity && <RequiredMark />}
        </span>
        {!editing || !canEdit ? (
          <p>
            <Severity severity={displaySeverity}>
              {unlessLoading(
                displaySeverity
                  ? translations.severityGrades[displaySeverity]
                  : '-'
              )}
            </Severity>
          </p>
        ) : (
          <Editor>
            <Select
              value={form.severity}
              options={severityOptions}
              onChange={value => onChange('severity')(value)}
              height="3rem"
              grayed
              fullWidth
              nullable
              error={errors.severity}
            />
          </Editor>
        )}
      </Info>

      <Info>
        <span>{translations.responsible}</span>
        {!editing || !canEdit ? (
          <p>
            {unlessLoading(
              <PlainLink
                to={responsible ? `/users/${responsible?.id}` : '#'}
                $hoverColor="secondary"
              >
                {responsible?.fullName ?? '-'}
              </PlainLink>
            )}
          </p>
        ) : canEdit ? (
          <ResponsibleWrapper>
            <Editor>
              <UserSelect
                value={temporaryResponsible?.id ?? null}
                dropdownPageName="issue_category_responsible"
                height="3rem"
                onChange={(value, option) => {
                  onChange('responsibleUser')(value)
                  if (value === null)
                    setTemporaryResponsible(category?.responsibleUser ?? null)
                  else setTemporaryResponsible(option?.extra ?? null)
                }}
                fullWidth
                nullable={
                  temporaryResponsible?.id !== category?.responsibleUser?.id
                }
                grayed
              />
            </Editor>
          </ResponsibleWrapper>
        ) : (
          <p>
            {temporaryResponsible ? (
              <PlainLink
                to={`/users/${temporaryResponsible.id}`}
                $hoverColor="secondary"
              >
                {temporaryResponsible.fullName}
              </PlainLink>
            ) : (
              translations.none
            )}
          </p>
        )}
      </Info>

      <Info>
        <span>{translations.caseHandler}</span>
        {!editing || !canEdit ? (
          <p>
            {unlessLoading(
              <PlainLink
                to={caseHandler ? `/users/${caseHandler?.id}` : '#'}
                $hoverColor="secondary"
              >
                {caseHandler?.fullName ?? '-'}
              </PlainLink>
            )}
          </p>
        ) : (
          <ResponsibleWrapper>
            <Editor>
              <UserSelect
                value={form.caseHandler}
                dropdownPageName="issue_category_responsible"
                height="3rem"
                onChange={value => onChange('caseHandler')(value)}
                fullWidth
                nullable
                grayed
              />
            </Editor>
          </ResponsibleWrapper>
        )}
      </Info>

      <Info className="category">
        <span>
          {translations.category} {showEditMark && <RequiredMark />}
        </span>
        {!editing || !canEdit || form.forInfringements ? (
          <p>{unlessLoading(issueCategory)}</p>
        ) : (
          <Editor>
            <CategorySelect
              value={form.category}
              fullWidth
              onChange={handleChangeCategory}
            />
          </Editor>
        )}
        {!editing || !canEdit || form.forInfringements
          ? subCategory?.name && (
              <>
                <span>{translations.subCategory}</span>
                <p>{unlessLoading(subCategory?.name)}</p>
              </>
            )
          : form.category &&
            subcategoryOptions?.length > 0 && (
              <>
                <span>
                  {translations.subCategory} {showEditMark && <RequiredMark />}
                </span>
                <Select
                  loading={subcategoriesLoading}
                  value={form.subcategory}
                  options={subcategoryOptions}
                  onChange={onChange('subcategory')}
                  height="3rem"
                  grayed
                  fullWidth
                  nullable
                  error={errors.subcategory}
                />
              </>
            )}
      </Info>

      <Info>
        <span>
          {translations.riskAspect}{' '}
          {showEditMark && requireRiskAspect && <RequiredMark />}
        </span>
        {!editing || !canEdit ? (
          <p>{unlessLoading(riskAspect?.name ?? '-')}</p>
        ) : (
          <Editor>
            <IssueRiskAspectSelect
              value={form.riskAspect}
              onChange={value => onChange('riskAspect')(value)}
              height="3rem"
              fullWidth
              nullable
              grayed
              error={errors.riskAspect}
            />
          </Editor>
        )}
      </Info>

      {visibleRelations.map(relation => {
        const selectedId = form.relationOptions[relation.id] ?? null
        const selectedName =
          relation.options.edges.find(e => e.node.id === selectedId)?.node
            .name ?? '-'
        return (
          <Info key={relation.id}>
            <span>
              {relation.name}
              {relation.required ? (
                <span style={{ color: 'red' }}>*</span>
              ) : editing && canEdit ? (
                <Icon
                  icon="times"
                  size="lg"
                  cursor="pointer"
                  color="gray6"
                  hoverColor="red"
                  margin="0 0 0 auto"
                  onClick={() => removeOptional(relation.id)}
                  aria-label="remove"
                />
              ) : null}
            </span>
            {!editing || !canEdit ? (
              <p>{unlessLoading(selectedName)}</p>
            ) : (
              <Editor>
                <IssueRelationField
                  relation={relation}
                  value={selectedId}
                  onChange={id => setRelationOption(relation.id, id)}
                />
              </Editor>
            )}
          </Info>
        )
      })}

      {editing && canEdit && availableOptionalRelations.length > 0 && (
        <Info>
          <span>{translations.addRelation}</span>
          <Editor>
            <Select
              value={null}
              options={availableOptionalRelations.map(r => ({
                label: r.name,
                value: r.id,
              }))}
              onChange={(id: string | null) => id && addOptional(id)}
              placeholder={translations.addRelation}
              fullWidth
              grayed
              height="3rem"
              nullable
            />
          </Editor>
        </Info>
      )}

      {/* Filler cells so the last row before .date is always full width.
          Pre-relation cells: 8 (status, user, reference, severity,
          responsible, caseHandler, category, riskAspect). Plus visible
          relations and the "Add" picker (if shown). */}
      {Array.from({
        length:
          (3 -
            ((8 +
              visibleRelations.length +
              (editing && canEdit && availableOptionalRelations.length > 0
                ? 1
                : 0)) %
              3)) %
          3,
      }).map((_, i) => (
        <Info key={`filler-${i}`} aria-hidden />
      ))}

      <Info
        className="date"
        title={date ? format(new Date(date), 'dd.MM.yyyy HH:mm') : undefined}
      >
        {!editing || form.forInfringements ? (
          <>
            <span>{translations.date}</span>
            <p>
              {unlessLoading(format(new Date(date ?? 0), 'PP, HH:mm'))}-
              {unlessLoading(format(new Date(dateEnd ?? 0), 'PP, HH:mm'))}
            </p>
          </>
        ) : (
          <TimeContainer>
            <div>
              <TimeLabel>{translations.dataFrom}</TimeLabel>
              <DateEditor>
                <TimePicker value={form.date} onChange={onChange('date')} />
                <DatePicker
                  mode="single"
                  value={form.date}
                  onChange={date =>
                    date ? handleChangeDate(date, 'date') : undefined
                  }
                />
              </DateEditor>
            </div>
            <div>
              <TimeLabel>{translations.dataTo}</TimeLabel>
              <DateEditor>
                <TimePicker
                  value={form.dateEnd}
                  onChange={onChange('dateEnd')}
                />
                <DatePicker
                  mode="single"
                  value={form.dateEnd}
                  onChange={date =>
                    date ? handleChangeDate(date, 'dateEnd') : undefined
                  }
                />
              </DateEditor>
            </div>
          </TimeContainer>
        )}
      </Info>
    </Wrapper>
  )
}
